/**
 * Technical line drawings for the revealed background layer.
 *
 * Every shape is stroke-only and draws with `currentColor`, so the whole set
 * inherits one colour from the theme.
 * They read as the schematics behind a PV installation: modules, strings,
 * inverter, storage, wallbox, yield curve.
 */

/**
 * Repeating PV module cell pattern — the "blueprint grid" of this project.
 *
 * Deliberately has no viewBox: user units then equal CSS pixels, so the cells
 * stay square at every viewport. A stretched viewBox (preserveAspectRatio
 * "none") turns them into tall rectangles, which reads as a rendering fault.
 */
export function CellGrid() {
  return (
    <svg className="solar-bg__grid" width="100%" height="100%">
      <defs>
        <pattern id="pv-cell" x="0" y="0" width="34" height="34" patternUnits="userSpaceOnUse">
          <rect x="2" y="2" width="30" height="30" rx="2.5" fill="none" stroke="currentColor" strokeWidth="0.8" />
          {/* busbars across each cell */}
          <line x1="12" y1="2" x2="12" y2="32" stroke="currentColor" strokeWidth="0.45" />
          <line x1="22" y1="2" x2="22" y2="32" stroke="currentColor" strokeWidth="0.45" />
        </pattern>
        <pattern id="pv-module" x="0" y="0" width="204" height="136" patternUnits="userSpaceOnUse">
          <rect x="0" y="0" width="204" height="136" fill="none" stroke="currentColor" strokeWidth="1.4" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#pv-cell)" />
      <rect width="100%" height="100%" fill="url(#pv-module)" />
    </svg>
  );
}

/** House elevation with a tilted module field and incoming irradiance. */
export function RoofElevation() {
  return (
    <svg className="solar-bg__shape shape-roof" viewBox="0 0 200 140" fill="none">
      <path d="M20 80 L100 28 L180 80" stroke="currentColor" strokeWidth="0.9" />
      <path d="M32 80 L32 128 L168 128 L168 80" stroke="currentColor" strokeWidth="0.7" />
      <rect x="60" y="96" width="26" height="32" stroke="currentColor" strokeWidth="0.5" />
      <rect x="112" y="98" width="30" height="22" stroke="currentColor" strokeWidth="0.5" />
      {/* module field on the pitched roof */}
      <path d="M46 74 L100 39 L154 74 L100 74 Z" stroke="currentColor" strokeWidth="0.6" />
      <path d="M64 62 L118 62M82 51 L136 51M73 74 L100 39M100 74 L127 51" stroke="currentColor" strokeWidth="0.35" />
      {/* irradiance */}
      <path d="M150 8 L142 20M168 14 L156 24M176 30 L162 36" stroke="currentColor" strokeWidth="0.5" strokeLinecap="round" />
    </svg>
  );
}

/** String wiring: modules in series feeding an inverter and the grid. */
export function StringDiagram() {
  return (
    <svg className="solar-bg__shape shape-string" viewBox="0 0 240 90" fill="none">
      {[0, 46, 92].map((x) => (
        <g key={x}>
          <rect x={10 + x} y="12" width="34" height="24" stroke="currentColor" strokeWidth="0.6" />
          <line x1={19 + x} y1="12" x2={19 + x} y2="36" stroke="currentColor" strokeWidth="0.25" />
          <line x1={27 + x} y1="12" x2={27 + x} y2="36" stroke="currentColor" strokeWidth="0.25" />
          <line x1={35 + x} y1="12" x2={35 + x} y2="36" stroke="currentColor" strokeWidth="0.25" />
        </g>
      ))}
      <path d="M44 24 L56 24M90 24 L102 24M136 24 L148 24" stroke="currentColor" strokeWidth="0.5" />
      {/* inverter */}
      <rect x="150" y="8" width="34" height="32" rx="2" stroke="currentColor" strokeWidth="0.7" />
      <path d="M156 30 q5 -12 10 0 t10 0" stroke="currentColor" strokeWidth="0.45" fill="none" />
      <path d="M156 18 h20" stroke="currentColor" strokeWidth="0.45" />
      {/* to grid */}
      <path d="M167 40 L167 62 L212 62" stroke="currentColor" strokeWidth="0.5" />
      <path d="M212 52 L212 72M206 56 L218 56M206 68 L218 68" stroke="currentColor" strokeWidth="0.5" />
    </svg>
  );
}

/** Battery storage cabinet with stacked cells and a charge indicator. */
export function StorageStack() {
  return (
    <svg className="solar-bg__shape shape-storage" viewBox="0 0 90 140" fill="none">
      <rect x="12" y="10" width="66" height="120" rx="3" stroke="currentColor" strokeWidth="0.8" />
      {[26, 50, 74, 98].map((y) => (
        <rect key={y} x="22" y={y} width="46" height="18" rx="1.5" stroke="currentColor" strokeWidth="0.45" />
      ))}
      <path d="M45 30 L39 42 L47 42 L41 54" stroke="currentColor" strokeWidth="0.5" fill="none" />
      <line x1="22" y1="20" x2="42" y2="20" stroke="currentColor" strokeWidth="0.4" />
    </svg>
  );
}

/** Wallbox with a coiled cable and a Type-2 connector. */
export function Wallbox() {
  return (
    <svg className="solar-bg__shape shape-wallbox" viewBox="0 0 120 130" fill="none">
      <rect x="14" y="10" width="46" height="66" rx="4" stroke="currentColor" strokeWidth="0.8" />
      <rect x="24" y="22" width="26" height="16" rx="1" stroke="currentColor" strokeWidth="0.45" />
      <path d="M33 48 L29 58 L36 58 L32 68" stroke="currentColor" strokeWidth="0.5" fill="none" />
      <path d="M60 62 q28 6 22 26 q-6 20 18 24" stroke="currentColor" strokeWidth="0.55" fill="none" />
      <circle cx="102" cy="116" r="9" stroke="currentColor" strokeWidth="0.6" />
      <circle cx="99" cy="113" r="1.6" stroke="currentColor" strokeWidth="0.4" />
      <circle cx="105" cy="113" r="1.6" stroke="currentColor" strokeWidth="0.4" />
      <circle cx="102" cy="119" r="1.6" stroke="currentColor" strokeWidth="0.4" />
    </svg>
  );
}

/** Daily generation curve with axes and hour ticks. */
export function YieldCurve() {
  return (
    <svg className="solar-bg__shape shape-yield" viewBox="0 0 200 110" fill="none">
      <path d="M18 92 L188 92M18 92 L18 12" stroke="currentColor" strokeWidth="0.6" />
      <path d="M18 92 C 58 92, 62 24, 100 24 C 138 24, 146 92, 188 92" stroke="currentColor" strokeWidth="0.8" />
      <path d="M46 92 L46 86M74 92 L74 86M100 92 L100 86M128 92 L128 86M156 92 L156 86" stroke="currentColor" strokeWidth="0.4" />
      <path d="M18 60 L188 60" stroke="currentColor" strokeWidth="0.3" strokeDasharray="3 4" />
      <circle cx="100" cy="24" r="2" stroke="currentColor" strokeWidth="0.5" />
    </svg>
  );
}

/** Sun with radiating rays — the irradiance source. */
export function SunDial() {
  return (
    <svg className="solar-bg__shape shape-sun" viewBox="0 0 110 110" fill="none">
      <circle cx="55" cy="55" r="20" stroke="currentColor" strokeWidth="0.8" />
      <circle cx="55" cy="55" r="30" stroke="currentColor" strokeWidth="0.3" strokeDasharray="2 5" />
      {Array.from({ length: 12 }, (_, i) => {
        const a = (i * Math.PI) / 6;
        return (
          <line
            key={i}
            x1={55 + Math.cos(a) * 36}
            y1={55 + Math.sin(a) * 36}
            x2={55 + Math.cos(a) * 46}
            y2={55 + Math.sin(a) * 46}
            stroke="currentColor"
            strokeWidth="0.6"
            strokeLinecap="round"
          />
        );
      })}
    </svg>
  );
}

/** Balcony power plant: railing with two small modules. */
export function BalconyPlant() {
  return (
    <svg className="solar-bg__shape shape-balcony" viewBox="0 0 160 90" fill="none">
      <path d="M8 78 L152 78M8 20 L8 78M152 20 L152 78M8 20 L152 20" stroke="currentColor" strokeWidth="0.6" />
      {[30, 62, 94, 126].map((x) => (
        <line key={x} x1={x} y1="20" x2={x} y2="78" stroke="currentColor" strokeWidth="0.3" />
      ))}
      <rect x="18" y="30" width="56" height="34" stroke="currentColor" strokeWidth="0.7" />
      <rect x="86" y="30" width="56" height="34" stroke="currentColor" strokeWidth="0.7" />
      <path d="M32 30 L32 64M46 30 L46 64M60 30 L60 64M100 30 L100 64M114 30 L114 64M128 30 L128 64" stroke="currentColor" strokeWidth="0.25" />
    </svg>
  );
}
